import React from 'react';

function normalizeState(state) {
  if (state === 'current' || state === 'delayed' || state === 'stale') {
    return state;
  }
  return 'unknown';
}

export function formatEvidenceDateTime(value) {
  if (!value) {
    return 'Not available yet';
  }
  return new Date(value).toLocaleString();
}

export function evidenceStateLabel(state) {
  switch (normalizeState(state)) {
    case 'current':
      return 'Current';
    case 'delayed':
      return 'Delayed';
    case 'stale':
      return 'Stale';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

export function evidenceChipClassName(state) {
  return `chip evidence-state-chip evidence-state-${normalizeState(state)}`;
}

function defaultSummary(state) {
  switch (normalizeState(state)) {
    case 'current':
      return 'Runtime evidence is arriving within the expected cadence.';
    case 'delayed':
      return 'Runtime evidence is arriving, but later than the expected cadence.';
    case 'stale':
      return 'Runtime evidence is older than the expected cadence.';
    case 'unknown':
    default:
      return 'Runtime freshness cannot be determined yet.';
  }
}

function postureNotes(posture) {
  if (!posture) {
    return [];
  }
  return Array.from(
    new Set(
      [posture.partial_data_reason, posture.stale_reason, posture.low_data_reason]
        .filter(Boolean)
        .map((value) => String(value))
    )
  );
}

export function evidenceSummary(posture) {
  return postureNotes(posture)[0] || defaultSummary(posture?.state);
}

export function EvidencePostureChip({ posture, prefix = null }) {
  const label = evidenceStateLabel(posture?.state);
  return (
    <span className={evidenceChipClassName(posture?.state)}>
      {prefix ? `${prefix}: ${label}` : label}
    </span>
  );
}

export function EvidencePostureCard({
  posture,
  title = 'Runtime freshness',
  showScopeSummary = true,
  maxScopes = null,
}) {
  const scopes = posture?.scopes || [];
  const visibleScopes = typeof maxScopes === 'number' ? scopes.slice(0, maxScopes) : scopes;
  const notes = postureNotes(posture);

  return (
    <div className={`detail-card evidence-panel evidence-panel-${normalizeState(posture?.state)}`}>
      <div className="evidence-panel-header">
        <strong>{title}</strong>
        <EvidencePostureChip posture={posture} />
      </div>
      <p>{evidenceSummary(posture)}</p>
      <div className="evidence-meta">
        <p>
          Last collected <strong>{formatEvidenceDateTime(posture?.last_collected_at)}</strong>
        </p>
        <p>
          Last analysed <strong>{formatEvidenceDateTime(posture?.last_analysed_at)}</strong>
        </p>
      </div>
      {showScopeSummary && posture?.scope_summary ? <p className="overview-note">{posture.scope_summary}</p> : null}
      {notes.slice(1).map((note) => (
        <p className="overview-note" key={note}>
          {note}
        </p>
      ))}
      {visibleScopes.length ? (
        <div className="evidence-scope-grid">
          {visibleScopes.map((scope) => (
            <div className="detail-card evidence-scope-card" key={`${scope.scope_type}-${scope.scope_id || scope.label}`}>
              <div className="workflow-mode-header">
                <strong>{scope.label}</strong>
                <span className={evidenceChipClassName(scope.state)}>{evidenceStateLabel(scope.state)}</span>
              </div>
              <p>
                Collected <strong>{formatEvidenceDateTime(scope.last_collected_at)}</strong>
              </p>
              <p>
                Analysed <strong>{formatEvidenceDateTime(scope.last_analysed_at)}</strong>
              </p>
              {scope.partial_data_reason ? <p className="overview-note">{scope.partial_data_reason}</p> : null}
              {!scope.partial_data_reason && scope.stale_reason ? <p className="overview-note">{scope.stale_reason}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
